export type BrandPalette = { base: string; shades: readonly string[] };

const fallbackBrandPalettes: readonly BrandPalette[] = [
  { base: '#2f8065', shades: ['#4b997d', '#73b99d', '#9bd1bc', '#c5e7d8'] },
  { base: '#b47a21', shades: ['#c9963b', '#dcaf62', '#ebc98d', '#f5dfbb'] },
  { base: '#4f7fa8', shades: ['#6e99bc', '#91b3cf', '#b4cde0', '#d4e3ee'] },
  { base: '#8b6fa8', shades: ['#a087bb', '#b69ecb', '#cbb9da', '#e0d4e9'] },
];

const fallbackProductColours = ['#2f8065', '#72b99d', '#d9a72f', '#4f7fa8', '#8b6fa8', '#d37b46'];

function shadeFromBrandColour(hex: string, amount: number) {
  const matchedColour = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!matchedColour) return hex;

  const value = Number.parseInt(matchedColour[1]!, 16);
  const blend = (channel: number) => Math.round(channel + (255 - channel) * amount)
    .toString(16)
    .padStart(2, '0');
  return `#${blend((value >> 16) & 255)}${blend((value >> 8) & 255)}${blend(value & 255)}`;
}

export function contrastTextColour(fill: string) {
  const matchedColour = /^#([0-9a-f]{6})$/i.exec(fill);
  if (!matchedColour) return '#14251e';

  const colour = Number.parseInt(matchedColour[1]!, 16);
  const red = (colour >> 16) & 255;
  const green = (colour >> 8) & 255;
  const blue = colour & 255;
  const luminance = (red * 0.299 + green * 0.587 + blue * 0.114) / 255;
  return luminance < 0.55 ? '#ffffff' : '#14251e';
}

export function paletteForBrand(themeColor: string | null, index: number): BrandPalette {
  const fallback = fallbackBrandPalettes[index % fallbackBrandPalettes.length] ?? fallbackBrandPalettes[0]!;
  if (!themeColor || !/^#[0-9a-f]{6}$/i.test(themeColor)) return fallback;

  return {
    base: themeColor,
    shades: [0.16, 0.31, 0.46, 0.61].map((amount) => shadeFromBrandColour(themeColor, amount)),
  };
}

export function productTreemapColours(rows: ReadonlyArray<{ brand_theme_color: string | null; product_theme_color?: string | null }>) {
  const productCountByBrand = new Map<string, number>();

  return rows.map((product, index) => {
    if (product.product_theme_color && /^#[0-9a-f]{6}$/i.test(product.product_theme_color)) {
      return product.product_theme_color;
    }
    const themeColor = product.brand_theme_color;
    if (!themeColor || !/^#[0-9a-f]{6}$/i.test(themeColor)) {
      return fallbackProductColours[index % fallbackProductColours.length]!;
    }

    const productIndex = productCountByBrand.get(themeColor) ?? 0;
    productCountByBrand.set(themeColor, productIndex + 1);
    return shadeFromBrandColour(themeColor, [0, 0.18, 0.36, 0.54][productIndex % 4]!);
  });
}
